/**
 * Builds the URL the client navigates to when it detects an expired session.
 *
 * Every redirect carries:
 *  - `RURL`         the page the customer was on, so CCUI can return them there after
 *                   re-authenticating. Without it they land on the account home page.
 *                   The name is CCUI's `Constants.QueryReturnUrl` ("RURL"), and the
 *                   casing is deliberate.
 *  - `expiredSesID` the session id that expired. CCUI mints a NEW session id as part
 *                   of the logout, so without this the expiring session and the logout
 *                   it caused cannot be correlated in telemetry.
 *  - `expiredPgID`  the page tracking id of the view that was open.
 *  - `expiredRID`   the request id of the 401 that triggered it, when the caller knows
 *                   it. This is the same value sent as `x-ms-requestid`, so it joins
 *                   the logout to a single middle-tier request.
 *  - `expiredSrc`   which client emitted the redirect. A dozen packages can trigger a
 *                   session-expired navigation and they are otherwise indistinguishable
 *                   once the browser has moved on.
 */

export interface SessionExpiredContext {
  /** Session id that expired, e.g. `serverContext.Omniture.SessionId`. */
  sessionId?: string | number | null;
  /** Page tracking id, e.g. `serverContext.PageTrackingId`. */
  pageTrackingId?: string | number | null;
  /** Request id of the 401 that triggered the redirect, when known. */
  requestId?: string | null;
  /** Package that emitted the redirect, e.g. `'ui-next'`. Include a sub-path when one package has several triggers. */
  source: string;
  /** Page to return to. Defaults to the current location. */
  returnUrl?: string;
}

/**
 * Tags a sign-out URL with the return URL and the identity of the expiring session.
 *
 * Call this at the moment of the redirect rather than caching the result: the return
 * URL must reflect the page the customer is on when the session expires, not the page
 * that happened to construct the caller.
 *
 * Never throws - diagnostics must not keep a signed-out customer off the login page.
 * A malformed base URL is returned unchanged.
 *
 * @param baseUrl - the sign-out URL, absolute or root-relative
 *                  (e.g. `https://ads.microsoft.com/default.aspx?reason=1&msg=sessionexpired`)
 * @param context - identity of the expiring session
 * @returns the tagged URL
 */
export function buildSessionExpiredUrl(baseUrl: string, context: SessionExpiredContext): string {
  if (!baseUrl) {
    return baseUrl;
  }

  try {
    // The base resolves root-relative sign-out URLs; searchParams handles the ?/&
    // join and the escaping, which matters because some callers already pass a query.
    const target = new URL(baseUrl, window.location.origin);
    const {
      sessionId, pageTrackingId, requestId, source, returnUrl,
    } = context;

    target.searchParams.set('RURL', returnUrl || window.location.href);

    if (sessionId) {
      target.searchParams.set('expiredSesID', String(sessionId));
    }

    if (pageTrackingId) {
      target.searchParams.set('expiredPgID', String(pageTrackingId));
    }

    if (requestId) {
      target.searchParams.set('expiredRID', String(requestId));
    }

    if (source) {
      target.searchParams.set('expiredSrc', source);
    }

    return target.href;
  } catch (e) {
    return baseUrl;
  }
}

function ensureSessionExpiredParams(baseUrl: string): string {
  if (!baseUrl) {
    return baseUrl;
  }

  try {
    const target = new URL(baseUrl, window.location.origin);

    if (!target.searchParams.has('reason')) {
      target.searchParams.set('reason', '1');
    }

    if (!target.searchParams.has('msg')) {
      target.searchParams.set('msg', 'sessionexpired');
    }

    return target.href;
  } catch (e) {
    return baseUrl;
  }
}

/**
 * Convenience wrapper for the common case: the caller holds a server context object
 * exposing `Omniture.SessionId` and `PageTrackingId`.
 *
 * @param baseUrl - the sign-out URL
 * @param serverContext - server context, or any object with those fields
 * @param options - source, and optionally the request id of the failing call
 * @returns the tagged URL
 */
export function buildSessionExpiredUrlFromContext(
  baseUrl: string,
  serverContext: { Omniture?: { SessionId?: string }; PageTrackingId?: string | number } | null | undefined,
  options: { source: string; requestId?: string | null }
): string {
  const { Omniture, PageTrackingId } = serverContext || {};

  return buildSessionExpiredUrl(ensureSessionExpiredParams(baseUrl), {
    sessionId: Omniture ? Omniture.SessionId : undefined,
    pageTrackingId: PageTrackingId,
    requestId: options.requestId,
    source: options.source,
  });
}
